import { useListPreferences, type PlainUserPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";

export const PRIVACY_POLICY_LAST_UPDATED_DATE = new Date("2025-05-16T00:00:00Z");

export enum NudgeKeys {
    "PRIVACY_POLICY_UPDATED" = "PRIVACY_POLICY_UPDATED",
    "SDK_RELEASE" = "SDK_RELEASE",
    "HOSTED_RUNNER" = "HOSTED_RUNNER",
    "ONA_ANNOUNCEMENT" = "ONA_ANNOUNCEMENT",
}

type Nudge =
    | { key: NudgeKeys.PRIVACY_POLICY_UPDATED }
    | { key: NudgeKeys.SDK_RELEASE }
    | { key: NudgeKeys.HOSTED_RUNNER }
    | { key: NudgeKeys.ONA_ANNOUNCEMENT };

/**
 * Computes the active nudge (if any).
 * This ensures we only ever render one nudge at a time.
 */
export function useNudges(): { active?: Nudge; isPending: boolean; error: Error | null } {
    const { data: user, isPending: isPendingUser, error: errorUser } = useAuthenticatedUser();
    const { preferences, isPending: isPendingPreferences, error: errorPreferences } = useListPreferences();

    if (!user) {
        return { isPending: isPendingUser, error: errorUser };
    }

    if (!preferences) {
        return { isPending: isPendingPreferences, error: errorPreferences };
    }

    if (!hasAcceptedCurrentPrivacyPolicy({ user, preferences })) {
        return {
            active: { key: NudgeKeys.PRIVACY_POLICY_UPDATED },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }
    
    if (!hasDismissedNudge(NudgeKeys.SDK_RELEASE, preferences)) {
        return {
            active: { key: NudgeKeys.SDK_RELEASE },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    if (!hasDismissedNudge(NudgeKeys.HOSTED_RUNNER, preferences)) {
        return {
            active: { key: NudgeKeys.HOSTED_RUNNER },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    if (!hasDismissedNudge(NudgeKeys.ONA_ANNOUNCEMENT, preferences)) {
        return {
            active: { key: NudgeKeys.ONA_ANNOUNCEMENT },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    return { isPending: isPendingPreferences, error: errorPreferences };
}

function hasDismissedNudge(key: string, preferences: PlainUserPreference[]): boolean {
    const savedPreference = preferences?.find((pref) => pref.key === key);
    if (!savedPreference?.value || savedPreference.value === "false") {
        return false;
    }

    return !!savedPreference?.value;
}

function hasAcceptedCurrentPrivacyPolicy(options: { user: PlainUser; preferences: PlainUserPreference[] }): boolean {
    const { user, preferences } = options;
    const preference = preferences?.find((pref) => pref.key === NudgeKeys.PRIVACY_POLICY_UPDATED.toString());

    // If the user was created after the last privacy policy update, we don't need to show the nudge
    if (user.createdAt?.seconds && user.createdAt?.seconds > PRIVACY_POLICY_LAST_UPDATED_DATE.getTime() / 1000) {
        return true;
    }

    // If the user has already accepted the current privacy policy, we don't need to show the nudge
    if (preference?.value) {
        const acceptedAt = new Date(preference?.value);
        if (acceptedAt > PRIVACY_POLICY_LAST_UPDATED_DATE) {
            return true;
        }
    }

    return false;
}
